import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface CourseProgress {
  title: string;
  slug: string;
  lessonCount: number;
  completedLessons: number;
  isRequired: boolean;
}

export interface LearningAreaContext {
  areaTitle: string;
  areaSlug: string;
  requiredCourses: CourseProgress[];
  optionalCourses: CourseProgress[];
  completedRequired: number;
  totalRequired: number;
  completedOptional: number;
  requiredOptionalCount: number;
}

/**
 * Detects if the user is on a learning area page and fetches progress context.
 * Returns null if not on a learning area route or data isn't available.
 */
export function useLearningAreaContext(): LearningAreaContext | null {
  const location = useLocation();

  // Extract slug from /mein-bereich/bereich/:slug
  const match = location.pathname.match(/^\/mein-bereich\/bereich\/([^/]+)/);
  const slug = match?.[1] ?? null;

  const { data } = useQuery({
    queryKey: ["bubble-learning-area-context", slug],
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!slug) return null;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;
      const user = session.user;

      // Get learning area by slug
      const { data: area } = await supabase
        .from("learning_areas")
        .select("id, title, slug")
        .eq("slug", slug)
        .single();

      if (!area) return null;

      // Get linked courses
      const { data: linkedCourses } = await supabase
        .from("learning_area_courses")
        .select("course_id, is_required, section_id, learning_area_sections(title)")
        .eq("learning_area_id", area.id);

      if (!linkedCourses || linkedCourses.length === 0) return null;

      // Get progress via RPC
      const { data: payload } = await supabase.rpc(
        "get_learning_area_courses_with_progress",
        { p_user_id: user.id, p_learning_area_id: area.id }
      );

      if (!payload) return null;

      const courses = ((payload as unknown as { courses?: any[] })?.courses ?? []) as any[];
      const courseById = new Map(courses.map((c) => [c.id, c]));

      const requiredCourses: CourseProgress[] = [];
      const optionalCourses: CourseProgress[] = [];

      for (const lc of linkedCourses) {
        const course = courseById.get(lc.course_id);
        if (!course) continue;

        const item: CourseProgress = {
          title: course.title,
          slug: course.slug,
          lessonCount: Number(course.lessonCount ?? 0),
          completedLessons: Number(course.completedLessons ?? 0),
          isRequired: lc.is_required || false,
        };

        const sectionTitle = (lc.learning_area_sections as any)?.title || "";
        if (lc.is_required) {
          requiredCourses.push(item);
        } else if (sectionTitle === "Selbstlernkurse") {
          optionalCourses.push(item);
        }
      }

      const isFachkraftMusik = slug === "fachkraft-fuer-musik";

      return {
        areaTitle: area.title,
        areaSlug: area.slug,
        requiredCourses,
        optionalCourses,
        completedRequired: requiredCourses.filter((c) => c.lessonCount > 0 && c.completedLessons >= c.lessonCount).length,
        totalRequired: requiredCourses.length,
        completedOptional: optionalCourses.filter((c) => c.lessonCount > 0 && c.completedLessons >= c.lessonCount).length,
        requiredOptionalCount: isFachkraftMusik ? 3 : 0,
      } as LearningAreaContext;
    },
  });

  return data ?? null;
}
